/**
 * HFDS — Headzflex Design System
 * Component: HF-014 Complete the Look
 * Version: 1.0.0
 *
 * Responsibilities:
 * - Control horizontal product navigation.
 * - Update arrow disabled states.
 * - Emit analytics-ready product click events.
 */

(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-hf-complete-look]";

  class HFCompleteLook {
    /**
     * @param {HTMLElement} root
     */
    constructor(root) {
      this.root = root;

      this.list = root.querySelector(
        "[data-hf-complete-look-list]"
      );

      this.previousButton = root.querySelector(
        "[data-hf-complete-look-previous]"
      );

      this.nextButton = root.querySelector(
        "[data-hf-complete-look-next]"
      );

      this.productLinks = Array.from(
        root.querySelectorAll(
          "[data-hf-complete-look-product]"
        )
      );

      this.handlePrevious =
        this.handlePrevious.bind(this);

      this.handleNext =
        this.handleNext.bind(this);

      this.handleScroll =
        this.handleScroll.bind(this);

      this.handleResize =
        this.handleResize.bind(this);

      this.handleProductClick =
        this.handleProductClick.bind(this);

      this.initialize();
    }

    initialize() {
      if (!window.HFDS) {
        console.error(
          "[HFDS Complete Look] HFDS core is unavailable."
        );

        return;
      }

      if (!(this.list instanceof HTMLElement)) {
        return;
      }

      this.previousButton?.addEventListener(
        "click",
        this.handlePrevious
      );

      this.nextButton?.addEventListener(
        "click",
        this.handleNext
      );

      this.list.addEventListener(
        "scroll",
        this.handleScroll,
        {
          passive: true
        }
      );

      window.addEventListener(
        "resize",
        this.handleResize,
        {
          passive: true
        }
      );

      this.productLinks.forEach((link) => {
        link.addEventListener(
          "click",
          this.handleProductClick
        );
      });

      this.updateNavigation();

      this.root.dataset.hfInitialized = "true";
    }

    getScrollDistance() {
      const firstItem = this.list.querySelector(
        ".hf-complete-look__item"
      );

      if (!(firstItem instanceof HTMLElement)) {
        return this.list.clientWidth * 0.8;
      }

      const styles = window.getComputedStyle(this.list);

      const gap =
        Number.parseFloat(styles.columnGap) || 0;

      return firstItem.getBoundingClientRect().width + gap;
    }

    handlePrevious() {
      this.list.scrollBy({
        left: -this.getScrollDistance(),
        behavior: window.HFDS.prefersReducedMotion()
          ? "auto"
          : "smooth"
      });
    }

    handleNext() {
      this.list.scrollBy({
        left: this.getScrollDistance(),
        behavior: window.HFDS.prefersReducedMotion()
          ? "auto"
          : "smooth"
      });
    }

    handleScroll() {
      window.requestAnimationFrame(() => {
        this.updateNavigation();
      });
    }

    handleResize() {
      this.updateNavigation();
    }

    updateNavigation() {
      if (!this.previousButton || !this.nextButton) {
        return;
      }

      const maximumScroll =
        this.list.scrollWidth -
        this.list.clientWidth;

      const currentScroll =
        Math.abs(this.list.scrollLeft);

      this.previousButton.disabled =
        currentScroll <= 2;

      this.nextButton.disabled =
        currentScroll >= maximumScroll - 2;
    }

    handleProductClick(event) {
      const link = event.currentTarget;

      if (!(link instanceof HTMLElement)) {
        return;
      }

      window.HFDS.emit(
        "complete-look:product-click",
        {
          currentProductId:
            this.root.dataset.productId || "",
          recommendedProductId:
            link.dataset.productId || "",
          recommendedPosition:
            Number(link.dataset.position || 0),
          recommendedType:
            link.dataset.recommendationType || ""
        },
        this.root
      );
    }

    destroy() {
      this.previousButton?.removeEventListener(
        "click",
        this.handlePrevious
      );

      this.nextButton?.removeEventListener(
        "click",
        this.handleNext
      );

      this.list?.removeEventListener(
        "scroll",
        this.handleScroll
      );

      window.removeEventListener(
        "resize",
        this.handleResize
      );

      this.productLinks.forEach((link) => {
        link.removeEventListener(
          "click",
          this.handleProductClick
        );
      });

      delete this.root.dataset.hfInitialized;
    }
  }

  function initializeCompleteLook(
    context = document
  ) {
    if (!window.HFDS) {
      return;
    }

    window.HFDS
      .queryAll(ROOT_SELECTOR, context)
      .forEach((root) => {
        if (!(root instanceof HTMLElement)) {
          return;
        }

        if (root.dataset.hfInitialized === "true") {
          return;
        }

        root.hfCompleteLook =
          new HFCompleteLook(root);
      });
  }

  window.HFDS?.ready(() => {
    initializeCompleteLook(document);
  });

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeCompleteLook(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      window.HFDS
        ?.queryAll(ROOT_SELECTOR, event.target)
        .forEach((root) => {
          root.hfCompleteLook?.destroy();
        });
    }
  );

  window.HFDSCompleteLook = Object.freeze({
    version: "1.0.0",
    initialize: initializeCompleteLook
  });
})();